/* ==========================================================================
   Elite Male Wellness — Landing Page Core JS
   Smooth scroll · Hotspot explorer · Video facade
   Safe to load once per page. Re-runs on Shopify editor section events.
   ========================================================================== */
(function () {
  'use strict';

  /* Media-query helper.
     A bare window.matchMedia() call at module scope will throw in any
     environment that lacks it — and because this file is one IIFE, a throw
     here silently kills every feature below it. Guarding once, here, means a
     missing API degrades one behaviour instead of the whole page. */
  function mq(query) {
    if (typeof window.matchMedia !== 'function') return false;
    try { return window.matchMedia(query).matches; } catch (e) { return false; }
  }

  var reduceMotion = mq('(prefers-reduced-motion: reduce)');

  /* ----------------------------------------------------------------------
     1. Smooth scroll for in-page anchors (the "keep scrolling" link)
     Offsets for any sticky header so the target heading isn't hidden.
     --------------------------------------------------------------------*/
  function stickyOffset() {
    var offset = 0;
    var candidates = document.querySelectorAll(
      '.emw-adbar.is-sticky, .section-header.shopify-section-header-sticky, sticky-header, [data-emw-sticky]'
    );
    candidates.forEach(function (el) {
      var style = window.getComputedStyle(el);
      if (style.position === 'sticky' || style.position === 'fixed') {
        offset = Math.max(offset, el.getBoundingClientRect().height);
      }
    });
    return offset + 16;
  }

  function scrollToTarget(target) {
    if (!target) return;
    var top = target.getBoundingClientRect().top + window.pageYOffset - stickyOffset();
    window.scrollTo({ top: top, behavior: reduceMotion ? 'auto' : 'smooth' });
    // Keep keyboard focus in sync with the visual jump.
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }

  document.addEventListener('click', function (event) {
    var link = event.target.closest('a[href^="#"], [data-emw-scroll-to]');
    if (!link) return;

    var id = link.getAttribute('data-emw-scroll-to') || link.getAttribute('href');
    if (!id || id === '#') return;
    id = id.replace(/^#/, '');

    // Only intercept links inside (or pointing at) our landing page sections.
    var target = document.getElementById(id);
    if (!target) return;
    if (!target.closest('.emw') && !link.closest('.emw')) return;

    event.preventDefault();
    scrollToTarget(target);
    if (history.replaceState) history.replaceState(null, '', '#' + id);
  });

  /* ----------------------------------------------------------------------
     2. Hotspot explorer
     Desktop: hover or click a pin -> anchored popover.
     Mobile: chip row + panel below the diagram (hover doesn't exist on touch).
     Keyboard: pins are real buttons; arrow keys move between them.
     --------------------------------------------------------------------*/
  function initExplorer(root) {
    if (root.dataset.emwReady === 'true') return;
    root.dataset.emwReady = 'true';

    var pins = Array.prototype.slice.call(root.querySelectorAll('.emw-explorer__pin'));
    var pops = root.querySelectorAll('.emw-explorer__pop');
    var chips = Array.prototype.slice.call(root.querySelectorAll('.emw-explorer__chip'));
    var panel = root.querySelector('.emw-explorer__panel');
    if (!pins.length) return;

    var isDesktop = function () { return mq('(min-width: 900px)'); };

    function closeAll() {
      pins.forEach(function (p) { p.setAttribute('aria-expanded', 'false'); p.classList.remove('is-active'); });
      pops.forEach(function (p) { p.classList.remove('is-open'); });
    }

    function placePopover(pin, pop) {
      // Flip the popover to whichever side has room inside the frame.
      var x = parseFloat(pin.dataset.x);
      var y = parseFloat(pin.dataset.y);
      pop.style.top = y + '%';
      if (x > 55) {
        pop.style.left = 'auto';
        pop.style.right = (100 - x) + '%';
        pop.style.marginRight = '26px';
        pop.style.marginLeft = '0';
      } else {
        pop.style.right = 'auto';
        pop.style.left = x + '%';
        pop.style.marginLeft = '26px';
        pop.style.marginRight = '0';
      }
      pop.style.translate = '0 -50%';
    }

    function open(index, opts) {
      var pin = pins[index];
      if (!pin) return;
      var silent = opts && opts.silent;
      closeAll();

      if (!silent) {
        pin.setAttribute('aria-expanded', 'true');
        pin.classList.add('is-active');

        var pop = root.querySelector('.emw-explorer__pop[data-index="' + index + '"]');
        if (pop && isDesktop()) {
          placePopover(pin, pop);
          pop.classList.add('is-open');
        }
      }

      chips.forEach(function (c, i) { c.classList.toggle('is-active', i === index); });

      if (panel) {
        panel.querySelector('[data-panel-title]').textContent = pin.dataset.title || '';
        panel.querySelector('[data-panel-text]').textContent = pin.dataset.text || '';

        var imgs = panel.querySelectorAll('[data-panel-img]');
        if (imgs.length) {
          var matched = false;
          imgs.forEach(function (img) {
            var on = img.dataset.panelImg === String(index);
            img.classList.toggle('is-active', on);
            if (on) matched = true;
          });
          var media = panel.querySelector('[data-panel-media]');
          if (media) media.hidden = !matched;
        }
      }

      if (opts && opts.focus) pin.focus();
      root.dataset.activeIndex = String(index);
    }

    pins.forEach(function (pin, index) {
      pin.addEventListener('mouseenter', function () { if (isDesktop()) open(index); });
      pin.addEventListener('mouseleave', function () {
        // Fade the popover out as soon as the pointer leaves the pin, otherwise
        // it lingers and covers neighbouring pins until another is hovered.
        if (isDesktop()) closeAll();
      });
      pin.addEventListener('focus', function () { open(index); });
      pin.addEventListener('blur', function () { if (isDesktop()) closeAll(); });
      pin.addEventListener('click', function (event) {
        event.preventDefault();
        open(index);
      });
      pin.addEventListener('keydown', function (event) {
        var next = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % pins.length;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + pins.length) % pins.length;
        if (event.key === 'Escape') { closeAll(); return; }
        if (next !== null) { event.preventDefault(); open(next, { focus: true }); }
      });
    });

    chips.forEach(function (chip, index) {
      chip.addEventListener('click', function () { open(index); });
    });

    // Leaving the diagram closes the popover, but the panel keeps its content.
    var stage = root.querySelector('.emw-explorer__stage');
    if (stage) {
      stage.addEventListener('mouseleave', function () { if (isDesktop()) closeAll(); });
    }

    // Populate the mobile panel without popping a card open on desktop.
    open(0, { silent: true });

    // One-time pulse when the diagram enters the viewport.
    if (!reduceMotion && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            root.classList.add('is-revealed');
            io.disconnect();
          }
        });
      }, { threshold: 0.35 });
      io.observe(root);
    }
  }

  /* ----------------------------------------------------------------------
     3. Video facade — poster image until clicked, then load the real player.
     Keeps the page fast for paid traffic (no third-party embed on load).
     --------------------------------------------------------------------*/
  function initVideo(root) {
    if (root.dataset.emwReady === 'true') return;
    root.dataset.emwReady = 'true';

    var poster = root.querySelector('.emw-video__poster');
    if (!poster) return;

    poster.addEventListener('click', function () {
      var frame = root.querySelector('.emw-video__frame');
      var markup = root.querySelector('template[data-emw-player]');
      if (!frame || !markup) return;

      frame.insertAdjacentHTML('beforeend', markup.innerHTML);
      poster.remove();

      var native = frame.querySelector('video');
      if (native) { native.play().catch(function () {}); }
    });
  }

  /* ----------------------------------------------------------------------
     4. Reading progress — measured against the article, not the whole page,
     so the bar doesn't sit at 60% before the reader has started.
     --------------------------------------------------------------------*/
  function initProgress(root) {
    if (root.dataset.emwReady === 'true') return;
    root.dataset.emwReady = 'true';

    var bar = root.querySelector('.emw-progress__bar');
    if (!bar) return;

    function measure() {
      var articles = document.querySelectorAll('.emw-article');
      if (!articles.length) return;

      var first = articles[0].getBoundingClientRect();
      var last = articles[articles.length - 1].getBoundingClientRect();
      var start = first.top + window.pageYOffset;
      var end = last.bottom + window.pageYOffset - window.innerHeight;
      var span = Math.max(end - start, 1);
      var pct = ((window.pageYOffset - start) / span) * 100;

      bar.style.width = Math.min(Math.max(pct, 0), 100) + '%';
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () { measure(); ticking = false; });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    measure();
  }

  /* ----------------------------------------------------------------------
     5. Gallery — main image + thumbnail strip.
     --------------------------------------------------------------------*/
  function initGallery(root) {
    if (root.dataset.emwReady === 'true') return;
    root.dataset.emwReady = 'true';

    var slides = root.querySelectorAll('.emw-gallery__slide');
    var thumbs = root.querySelectorAll('.emw-gallery__thumb');
    if (!slides.length) return;

    function show(index) {
      slides.forEach(function (s, i) { s.classList.toggle('is-active', i === index); });
      thumbs.forEach(function (t, i) {
        t.classList.toggle('is-active', i === index);
        t.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    thumbs.forEach(function (thumb, index) {
      thumb.addEventListener('click', function () { show(index); });
    });

    // Variant changes can point the gallery at a specific image.
    root.addEventListener('emw:showmedia', function (event) {
      var id = String(event.detail || '');
      slides.forEach(function (slide, i) {
        if (slide.dataset.mediaId === id) show(i);
      });
    });
  }

  /* ----------------------------------------------------------------------
     6. Buy box — variant selection, price, availability, quantity.
     --------------------------------------------------------------------*/
  function formatMoney(cents, format) {
    var amount = (cents / 100).toFixed(2);
    var parts = amount.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var value = parts.join('.');
    return (format || '${{amount}}').replace(/\{\{\s*amount\s*\}\}/, value);
  }

  function initBuyBox(root) {
    if (root.dataset.emwReady === 'true') return;
    root.dataset.emwReady = 'true';

    // Page-scoped promotional display. Liquid renders the discounted figure
    // on first paint; without this, the first update() would overwrite it with
    // variant.price and the strikethrough would vanish on any variant change.
    // Same integer maths as the Liquid so the two can never disagree.
    // Variant ids already in the cart, seeded from Liquid so the button is
    // correct on first paint rather than flickering after a fetch.
    var inCart = {};
    (root.dataset.inCartIds || '').split(',').forEach(function (id) {
      if (id) inCart[id] = true;
    });
    var inCartAction = root.dataset.inCartAction === 'cart' ? 'cart' : 'checkout';

    // Anything this page put in the cart counts, not just this product's own
    // variants. Accepting the upsell adds the KIT — a different product — so
    // the server-side loop above can never see it; the upsell section reports
    // that separately.
    // The page advertises a discounted price to every visitor, so every
    // visitor has to actually get it — not just the ones who arrived through
    // a discount permalink. Hitting /discount/CODE puts it on the session
    // without navigating, so ad tracking parameters survive.
    discountCode = (root.dataset.discountCode || '').trim();
    if (discountCode && typeof window.fetch === 'function') {
      var applied = false;
      try { applied = window.sessionStorage.getItem('emw-discount') === discountCode; }
      catch (e) { applied = false; }
      if (!applied) {
        fetch('/discount/' + encodeURIComponent(discountCode), { credentials: 'same-origin' })
          .then(function () {
            try { window.sessionStorage.setItem('emw-discount', discountCode); } catch (e) {}
          })
          .catch(function () {});
      }
    }

    var upsellEl = document.querySelector('[data-emw-upsell]');
    var pageItemInCart = !!(upsellEl && upsellEl.dataset.kitInCart === 'true');

    var promoPct = parseInt(root.dataset.promoPercent, 10);
    if (!(promoPct > 0 && promoPct < 100)) promoPct = 0;
    // Mirrors Shopify: floor the discount, then subtract. Flooring the price
    // instead is a cent low (20799 vs 20800 on 25999 at 20%).
    function shownPrice(cents) {
      return promoPct ? cents - Math.floor(cents * promoPct / 100) : cents;
    }

    var dataEl = root.querySelector('[data-emw-variants]');
    if (!dataEl) return;

    var variants;
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }
    if (!variants || !variants.length) return;

    var moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    var idInput = root.querySelector('[data-emw-variant-id]');
    var priceNow = root.querySelector('[data-emw-price-now]');
    var priceWas = root.querySelector('[data-emw-price-was]');
    var priceSave = root.querySelector('[data-emw-price-save]');
    var submit = root.querySelector('[data-emw-submit]');
    var gallery = root.querySelector('.emw-gallery');

    function selectedOptions() {
      var picked = [];
      root.querySelectorAll('[data-emw-option-index]').forEach(function (group) {
        var checked = group.querySelector('input:checked');
        picked[parseInt(group.dataset.emwOptionIndex, 10)] = checked ? checked.value : null;
      });
      return picked;
    }

    function findVariant(picked) {
      return variants.filter(function (v) {
        return picked.every(function (value, i) {
          return value === null || v.options[i] === value;
        });
      })[0];
    }

    function markAvailability(picked) {
      // For a single-option product this simply strikes out sold-out sizes.
      root.querySelectorAll('[data-emw-option-index]').forEach(function (group) {
        var index = parseInt(group.dataset.emwOptionIndex, 10);
        group.querySelectorAll('.emw-swatch').forEach(function (swatch) {
          var input = swatch.querySelector('input');
          var probe = picked.slice();
          probe[index] = input.value;
          var match = variants.filter(function (v) {
            return probe.every(function (value, i) {
              return value === null || v.options[i] === value;
            });
          })[0];
          var available = !!(match && match.available);
          swatch.classList.toggle('is-unavailable', !available);
          input.disabled = !available;
        });
      });
    }

    function update() {
      var picked = selectedOptions();
      var variant = findVariant(picked);
      markAvailability(picked);

      if (!variant) {
        if (submit) { submit.disabled = true; submit.textContent = submit.dataset.unavailableText || 'Unavailable'; }
        return;
      }

      if (idInput) idInput.value = variant.id;

      if (priceNow) {
        var nextPrice = formatMoney(shownPrice(variant.price), moneyFormat);
        var priceWrap = priceNow.closest('.emw-price');
        if (priceNow.textContent !== nextPrice && priceWrap && !reduceMotion) {
          // Brief fade out, swap, then rise back in — signals the change
          // without the number visibly counting or flickering.
          priceWrap.classList.add('is-updating');
          setTimeout(function () {
            priceNow.textContent = nextPrice;
            priceWrap.classList.remove('is-updating');
            priceNow.classList.remove('is-changed');
            void priceNow.offsetWidth;
            priceNow.classList.add('is-changed');
          }, 150);
        } else {
          priceNow.textContent = nextPrice;
        }
      }

      if (priceWas) {
        // With a promo running the strikethrough is the real list price and
        // the badge is the promo percentage. Otherwise fall back to the
        // product's own compare-at price.
        var wasCents = promoPct ? variant.price : variant.compare_at_price;
        var onSale = promoPct
          ? true
          : !!(variant.compare_at_price && variant.compare_at_price > variant.price);
        priceWas.hidden = !onSale;
        if (onSale) priceWas.textContent = formatMoney(wasCents, moneyFormat);
        if (priceSave) {
          priceSave.hidden = !onSale;
          if (onSale) {
            var pct = promoPct
              ? promoPct
              : Math.round((1 - variant.price / variant.compare_at_price) * 100);
            priceSave.textContent = 'Save ' + pct + '%';
          }
        }
      }

      if (submit) {
        submit.disabled = !variant.available;
        var inCartText = submit.dataset.inCartText;
        if (!variant.available) {
          submit.textContent = submit.dataset.soldOutText || 'Sold out';
        } else if ((inCart[variant.id] || pageItemInCart) && inCartText) {
          submit.textContent = inCartText;
        } else {
          submit.textContent = submit.dataset.defaultText || 'Add to cart';
        }
      }

      if (gallery && variant.featured_media_id) {
        gallery.dispatchEvent(new CustomEvent('emw:showmedia', { detail: variant.featured_media_id }));
      }

      // Keep the URL variant param in sync so the page is shareable.
      if (history.replaceState && root.dataset.updateUrl === 'true') {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
      }
    }

    root.querySelectorAll('[data-emw-option-index] input').forEach(function (input) {
      input.addEventListener('change', update);
    });

    // Quantity stepper
    var qtyInput = root.querySelector('[data-emw-qty]');
    root.querySelectorAll('[data-emw-qty-step]').forEach(function (button) {
      button.addEventListener('click', function () {
        if (!qtyInput) return;
        var step = parseInt(button.dataset.emwQtyStep, 10);
        var next = Math.max(1, (parseInt(qtyInput.value, 10) || 1) + step);
        qtyInput.value = next;
      });
    });

    /* --- Add to cart -------------------------------------------------------
       Adds the currently selected variant. Uses the AJAX cart so the page
       doesn't reload, then follows the section's "After adding to cart"
       setting. If fetch fails for any reason the native form submit still
       runs, so the button never becomes a dead end. */
    var form = root.querySelector('form[action*="/cart/add"]');
    if (form) {
      form.addEventListener('submit', function (event) {
        if (!window.fetch) return; // let the browser submit normally

        var variantId = idInput && idInput.value;
        if (!variantId) return;

        event.preventDefault();

        // Already in the cart: send them on rather than adding a second one.
        // No add means no upsell, which is right — they have already seen it.
        // The discount code rides the session, so /checkout keeps it.
        if ((inCart[variantId] || pageItemInCart) && submit && submit.dataset.inCartText && !submit.disabled) {
          window.location.href = withDiscount(inCartAction === 'cart' ? '/cart' : '/checkout');
          return;
        }

        var after = root.dataset.afterAdd || 'cart';
        var qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
        var original = submit ? submit.textContent : '';

        // Hand off to the upsell if one is configured and hasn't been shown.
        var upsell = getUpsell(variantId);
        if (upsell && upsellOpen && !(upsell.dataset.oncePerSession === 'true' && upsellSeen)) {
          upsellSeen = true;
          upsellOpen({ variantId: Number(variantId), quantity: qty });
          return;
        }

        if (submit) { submit.disabled = true; submit.textContent = 'Adding…'; }

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: qty }] })
        })
          .then(function (res) {
            if (!res.ok) throw new Error('add failed');
            return res.json();
          })
          .then(function () {
            if (after === 'checkout') { window.location.href = withDiscount('/checkout'); return; }
            if (after === 'cart') { window.location.href = withDiscount('/cart'); return; }

            // Stay on page: nudge the theme to refresh its cart UI.
            document.documentElement.dispatchEvent(
              new CustomEvent('cart:refresh', { bubbles: true })
            );
            document.dispatchEvent(new CustomEvent('cart:build'));
            var drawer = document.querySelector('cart-drawer, #CartDrawer, .js-drawer-open-cart');
            if (drawer && typeof drawer.open === 'function') drawer.open();

            // It is in the cart now, so the button becomes the way onward.
            inCart[variantId] = true;
            pageItemInCart = true;

            if (submit) {
              submit.textContent = 'Added ✓';
              setTimeout(function () {
                submit.disabled = false;
                submit.textContent = submit.dataset.inCartText || original;
              }, 1800);
            }
          })
          .catch(function () {
            // Fall back to a normal form post so the customer still gets through.
            if (submit) { submit.disabled = false; submit.textContent = original; }
            form.submit();
          });
      });
    }

    /* --- Size guide modal ---------------------------------------------- */
    // The modal is a sibling of the buy grid, inside the same .emw wrapper.
    var scope = root.closest('.emw');
    var modal = scope ? scope.querySelector('.emw-sizemodal') : null;

    if (modal) {
      var lastFocus = null;

      function markCurrent() {
        var picked = selectedOptions();
        modal.querySelectorAll('.emw-sizerow').forEach(function (row) {
          var value = row.dataset.sizeValue;
          row.classList.toggle('is-current', picked.indexOf(value) !== -1);

          // Grey out rows whose variant is sold out.
          var match = variants.filter(function (v) { return v.options.indexOf(value) !== -1; })[0];
          row.classList.toggle('is-unavailable', !!match && !match.available);
        });
      }

      function openModal(trigger) {
        lastFocus = trigger || document.activeElement;
        modal.hidden = false;
        // next frame so the transition runs
        requestAnimationFrame(function () { modal.classList.add('is-open'); });
        document.documentElement.classList.add('emw-modal-open');
        markCurrent();
        var first = modal.querySelector('.emw-sizemodal__close');
        if (first) first.focus();
      }

      function closeModal() {
        modal.classList.remove('is-open');
        document.documentElement.classList.remove('emw-modal-open');
        var done = function () { modal.hidden = true; };
        if (reduceMotion) done();
        else setTimeout(done, 240);
        if (lastFocus) lastFocus.focus();
      }

      root.querySelectorAll('[data-emw-size-calc]').forEach(function (trigger) {
        trigger.addEventListener('click', function (event) {
          event.preventDefault();
          openModal(trigger);
          // Opening size info is a real intent signal — the visitor is working
          // out whether this size fits them. An exit-intent offer can use it to
          // tell "weighing the size" apart from "weighing the price".
          if (trigger.hasAttribute('data-emw-size-info')) {
            document.dispatchEvent(new CustomEvent('emw:size-info', {
              detail: { at: Date.now() }
            }));
          }
        });
      });

      modal.querySelectorAll('[data-emw-modal-close]').forEach(function (button) {
        button.addEventListener('click', closeModal);
      });

      // Pick a size -> tick the matching radio and let the normal update run.
      modal.querySelectorAll('[data-emw-pick-size]').forEach(function (button) {
        button.addEventListener('click', function () {
          var value = button.dataset.emwPickSize;
          var input = null;

          root.querySelectorAll('[data-emw-option-index] input').forEach(function (candidate) {
            if (candidate.value === value) input = candidate;
          });

          if (input && !input.disabled) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }

          closeModal();

          // Bring the picker into view so the change is visible.
          var picker = root.querySelector('[data-emw-option-index]');
          if (picker) {
            picker.scrollIntoView({
              behavior: reduceMotion ? 'auto' : 'smooth',
              block: 'center'
            });
          }
        });
      });

      // Escape closes; Tab stays inside the panel.
      modal.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') { closeModal(); return; }
        if (event.key !== 'Tab') return;

        var focusable = modal.querySelectorAll('button:not([disabled])');
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first.focus();
        }
      });

      root.addEventListener('change', markCurrent);
    }

    // ---- Keep the button honest about what is in the cart ----------------
    // The cart can change without this section knowing: the theme's cart
    // drawer, a quantity change, a removal, another tab. Re-read /cart.js and
    // re-render rather than trusting the state we were rendered with.
    var syncing = false;
    function refreshCartState() {
      if (syncing || typeof window.fetch !== 'function') return;
      syncing = true;
      fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (cart) {
          if (!cart || !cart.items) return;
          inCart = {};
          cart.items.forEach(function (item) { inCart[item.variant_id] = true; });
          var kitId = upsellEl && upsellEl.dataset.kitVariant;
          pageItemInCart = !!(kitId && cart.items.some(function (item) {
            return String(item.variant_id) === String(kitId);
          }));
          update();
        })
        .catch(function () {})
        .then(function () { syncing = false; });
    }

    // Adds made outside this closure — the upsell's accept, decline and
    // X-dismiss paths all call /cart/add.js themselves. Update optimistically
    // so the button flips immediately, then reconcile against the real cart.
    document.addEventListener('emw:cart-added', function (event) {
      pageItemInCart = true;
      if (event.detail && event.detail.variantId) inCart[event.detail.variantId] = true;
      update();
      refreshCartState();
    });

    // The theme's own cart events, dispatched by its drawer and line-item
    // controls. These are the ones that were missing: remove the pump from the
    // drawer and the button has to stop offering checkout.
    ['cart:change', 'cart:refresh'].forEach(function (name) {
      document.addEventListener(name, refreshCartState);
      document.documentElement.addEventListener(name, refreshCartState);
    });

    // Back-navigation is served two different ways and only one of them fires
    // pageshow with persisted=true. The other replays a cached HTTP response:
    // pageshow says persisted=false, the Liquid-rendered cart state is from
    // whenever that copy was built, and nothing corrects it. Which one you get
    // varies by browser and headers, which is why the button looked
    // intermittent rather than simply broken.
    //
    // The Navigation Timing API names the navigation directly, so we can
    // re-verify on back/forward without paying a /cart.js request on every
    // ordinary page load. If the API is unavailable we verify anyway —
    // a wasted request is cheaper than a wrong button.
    function isHistoryNavigation() {
      try {
        if (typeof performance === 'undefined') return true;
        if (typeof performance.getEntriesByType === 'function') {
          var nav = performance.getEntriesByType('navigation')[0];
          if (nav && nav.type) return nav.type === 'back_forward';
        }
        if (performance.navigation) return performance.navigation.type === 2;
      } catch (e) {}
      return true;
    }
    if (isHistoryNavigation()) refreshCartState();

    window.addEventListener('pageshow', function (event) {
      if (event.persisted) refreshCartState();
    });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') refreshCartState();
    });

    update();
  }

  /* ----------------------------------------------------------------------
     7. Video cards — poster facade shared by the pair layout.
     --------------------------------------------------------------------*/
  function initVideoCard(root) {
    if (root.dataset.emwReady === 'true') return;
    root.dataset.emwReady = 'true';

    var poster = root.querySelector('.emw-vcard__poster');
    if (!poster) return;

    poster.addEventListener('click', function () {
      var frame = root.querySelector('.emw-vcard__frame');
      var markup = root.querySelector('template[data-emw-player]');
      if (!frame || !markup) return;
      frame.insertAdjacentHTML('beforeend', markup.innerHTML);
      poster.remove();
      var native = frame.querySelector('video');
      if (native) native.play().catch(function () {});
    });
  }

  /* ----------------------------------------------------------------------
     8. Testimonial grid — reveal the rest, staggered.
     --------------------------------------------------------------------*/
  function initGrid(root) {
    if (root.dataset.emwReady === 'true') return;
    root.dataset.emwReady = 'true';

    highlightReviews(root);

    var masonry = root.querySelector('.emw-tmasonry');
    var button = root.querySelector('.emw-showmore');
    if (!masonry) return;

    var step = parseInt(root.dataset.step, 10) || 6;
    var all = Array.prototype.slice.call(masonry.querySelectorAll('.emw-tcard'));
    if (!all.length) return;

    var shown = Math.min(step, all.length);
    var label = button ? button.querySelector('[data-emw-showmore-label]') : null;

    function columnCount() {
      if (mq('(min-width: 980px)')) return 3;
      if (mq('(min-width: 660px)')) return 2;
      return 1;
    }

    var columns = [];

    function buildColumns(count) {
      masonry.innerHTML = '';
      columns = [];
      for (var i = 0; i < count; i++) {
        var col = document.createElement('div');
        col.className = 'emw-tcol';
        masonry.appendChild(col);
        columns.push(col);
      }
      masonry.classList.add('is-columned');
    }

    function shortestColumn() {
      var best = columns[0];
      columns.forEach(function (c) {
        if (c.offsetHeight < best.offsetHeight) best = c;
      });
      return best;
    }

    // Place a card in the column that is currently shortest, so the grid
    // stays balanced and nothing already placed has to move.
    function place(card) {
      card.classList.remove('is-hidden');
      shortestColumn().appendChild(card);
    }

    function layout(count) {
      buildColumns(count);
      for (var i = 0; i < shown; i++) place(all[i]);
    }

    layout(columnCount());

    // Re-flow only when the breakpoint actually changes.
    var current = columnCount();
    window.addEventListener('resize', function () {
      var next = columnCount();
      if (next === current) return;
      current = next;
      layout(next);
    }, { passive: true });

    if (!button) return;

    button.addEventListener('click', function () {
      if (shown >= all.length) {
        // Collapse back to the opening set.
        shown = Math.min(step, all.length);
        all.slice(shown).forEach(function (card) {
          card.classList.add('is-hidden');
          card.classList.remove('is-entering');
          if (card.parentElement) card.parentElement.removeChild(card);
        });
        button.classList.remove('is-open');
        if (label) label.textContent = button.dataset.moreText || 'Show more reviews';
        masonry.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        return;
      }

      var next = all.slice(shown, shown + step);
      next.forEach(function (card, i) {
        place(card);
        if (!reduceMotion) {
          card.style.setProperty('--emw-stagger', (i * 60) + 'ms');
          card.classList.remove('is-entering');
          void card.offsetWidth;           // restart the animation
          card.classList.add('is-entering');
        }
      });
      shown += next.length;

      if (shown >= all.length) {
        button.classList.add('is-open');
        if (label) label.textContent = button.dataset.lessText || 'Show fewer reviews';
      }
    });
  }

  /* ----------------------------------------------------------------------
     11. Review keyword emphasis
     Bolds outcome language inside testimonial cards. Works on text nodes and
     builds real DOM nodes, so review copy can never break the markup or
     inject anything. Phrases are matched before single words so a phrase
     emphasises as one run instead of fragmenting into its parts.
     --------------------------------------------------------------------*/
  function escapeRegex(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function splitList(value) {
    return (value || '')
      .split(',')
      .map(function (s) { return s.trim(); })
      .filter(function (s) { return s.length > 0; });
  }

  function highlightReviews(root) {
    if (root.dataset.emphasis !== 'true') return;

    var phrases = splitList(root.dataset.phrases);
    var words = splitList(root.dataset.words);
    if (!phrases.length && !words.length) return;

    var byLength = function (a, b) { return b.length - a.length; };
    var terms = phrases.sort(byLength).concat(words.sort(byLength));

    var re;
    try {
      re = new RegExp('\\b(' + terms.map(escapeRegex).join('|') + ')\\b', 'gi');
    } catch (e) {
      return; // a malformed term should never take the section down
    }

    var cap = parseInt(root.dataset.emphasisCap, 10);
    if (isNaN(cap)) cap = 0;

    root.querySelectorAll('[data-raw]').forEach(function (el) {
      if (!el.dataset.original) el.dataset.original = el.textContent;
      var text = el.dataset.original;

      el.textContent = '';
      re.lastIndex = 0;

      var last = 0, hits = 0, match;
      while ((match = re.exec(text)) !== null) {
        if (cap > 0 && hits >= cap) break;
        if (match.index > last) {
          el.appendChild(document.createTextNode(text.slice(last, match.index)));
        }
        var mark = document.createElement('mark');
        mark.textContent = match[0];
        el.appendChild(mark);
        last = match.index + match[0].length;
        hits++;
        if (match[0].length === 0) re.lastIndex++;
      }
      if (last < text.length) {
        el.appendChild(document.createTextNode(text.slice(last)));
      }
    });
  }

  /* ----------------------------------------------------------------------
     9. FAQ — independent open/close. A panel closes only when its own
     header is clicked; opening one never closes another.
     --------------------------------------------------------------------*/
  function initFaq(root) {
    if (root.dataset.emwReady === 'true') return;
    root.dataset.emwReady = 'true';

    root.querySelectorAll('.emw-faq__q').forEach(function (button) {
      button.addEventListener('click', function () {
        var panel = document.getElementById(button.getAttribute('aria-controls'));
        if (!panel) return;
        var open = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', open ? 'false' : 'true');
        panel.classList.toggle('is-open', !open);
      });
    });
  }

  /* ----------------------------------------------------------------------
     10. Scroll reveal — one-shot, staggered within a group.
     --------------------------------------------------------------------*/
  function initReveal(scope) {
    var targets = (scope || document).querySelectorAll('.emw-reveal:not([data-emw-ready])');
    if (!targets.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.dataset.emwReady = 'true'; });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var siblings = Array.prototype.slice.call(el.parentElement.children).filter(function (n) {
          return n.classList.contains('emw-reveal');
        });
        el.style.setProperty('--emw-stagger', (siblings.indexOf(el) * 80) + 'ms');
        el.classList.remove('is-armed');
        el.classList.add('is-in');
        io.unobserve(el);
      });
    }, { threshold: 0.14, rootMargin: '0px 0px -8% 0px' });

    targets.forEach(function (el) {
      el.dataset.emwReady = 'true';

      // Anything already on screen stays visible — arming it would cause a
      // visible flash as it hid and then animated back in.
      var box = el.getBoundingClientRect();
      var onScreen = box.top < window.innerHeight && box.bottom > 0;
      if (onScreen) return;

      el.classList.add('is-armed');
      io.observe(el);
    });
  }


  /* ----------------------------------------------------------------------
     12. Count-up stat
     Animates a figure from a start value to its target when it first scrolls
     into view. Eased so it decelerates into the final number rather than
     ticking linearly. Runs once.
     --------------------------------------------------------------------*/
  function initCounters(scope) {
    var targets = (scope || document).querySelectorAll('[data-emw-count]:not([data-emw-ready])');
    if (!targets.length) return;

    function render(el, value, decimals) {
      var out = el.querySelector('[data-count-value]');
      if (out) out.textContent = value.toFixed(decimals);
    }

    function run(el) {
      var to = parseFloat(el.dataset.countTo);
      var from = parseFloat(el.dataset.countFrom);
      var decimals = parseInt(el.dataset.countDecimals, 10) || 0;
      var duration = parseInt(el.dataset.countDuration, 10) || 1800;

      if (isNaN(to)) return;
      if (isNaN(from)) from = 0;

      if (reduceMotion) { render(el, to, decimals); return; }

      var start = null;
      function frame(now) {
        if (start === null) start = now;
        var p = Math.min((now - start) / duration, 1);
        // easeOutCubic — moves at a readable pace then settles on the number.
        // Sharper curves (expo) reach ~80% in the first quarter and then crawl,
        // which reads as stalling rather than counting.
        var eased = 1 - Math.pow(1 - p, 3);
        render(el, from + (to - from) * eased, decimals);
        if (p < 1) requestAnimationFrame(frame);
        else render(el, to, decimals);
      }
      requestAnimationFrame(frame);
    }

    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.dataset.emwReady = 'true';
        render(el, parseFloat(el.dataset.countTo), parseInt(el.dataset.countDecimals, 10) || 0);
      });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        run(entry.target);
        io.unobserve(entry.target);
      });
    }, { threshold: 0.45 });

    targets.forEach(function (el) {
      el.dataset.emwReady = 'true';
      io.observe(el);
    });
  }


  /* ----------------------------------------------------------------------
     13. Gallery zoom + lightbox
     Desktop hover magnifies the active slide by tracking the pointer.
     Clicking opens the full-size image; clicking the backdrop closes it.
     --------------------------------------------------------------------*/
  function initZoom(root) {
    if (root.dataset.emwZoomReady === 'true') return;
    root.dataset.emwZoomReady = 'true';

    var main = root.querySelector('.emw-gallery__main');
    if (!main || !main.classList.contains('is-zoomable')) return;

    var canHover = mq('(hover: hover) and (min-width: 900px)');
    var level = (parseInt(main.dataset.zoomLevel, 10) || 20) / 10;
    main.style.setProperty('--emw-zoom', level);

    function activeImg() {
      var slide = main.querySelector('.emw-gallery__slide.is-active');
      return slide ? slide.querySelector('img') : null;
    }

    if (canHover) {
      main.addEventListener('mouseenter', function () { main.classList.add('is-zooming'); });
      main.addEventListener('mouseleave', function () { main.classList.remove('is-zooming'); });
      main.addEventListener('mousemove', function (event) {
        var box = main.getBoundingClientRect();
        var x = ((event.clientX - box.left) / box.width) * 100;
        var y = ((event.clientY - box.top) / box.height) * 100;
        main.style.setProperty('--emw-zx', x + '%');
        main.style.setProperty('--emw-zy', y + '%');
      });
    }

    // ---- Lightbox ----
    var box = document.createElement('div');
    box.className = 'emw-lightbox';
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.hidden = true;
    box.innerHTML = '<button type="button" class="emw-lightbox__close" aria-label="Close">&times;</button><img alt="">';
    document.body.appendChild(box);

    var boxImg = box.querySelector('img');
    var closeBtn = box.querySelector('.emw-lightbox__close');
    var lastFocus = null;

    function open() {
      var img = activeImg();
      if (!img) return;
      // currentSrc gives whichever srcset candidate the browser actually chose
      boxImg.src = img.currentSrc || img.src;
      boxImg.alt = img.alt || '';
      lastFocus = document.activeElement;
      box.hidden = false;
      requestAnimationFrame(function () { box.classList.add('is-open'); });
      document.documentElement.classList.add('emw-modal-open');
      closeBtn.focus();
    }

    function close() {
      box.classList.remove('is-open');
      document.documentElement.classList.remove('emw-modal-open');
      var done = function () { box.hidden = true; };
      if (reduceMotion) done(); else setTimeout(done, 260);
      if (lastFocus) lastFocus.focus();
    }

    main.addEventListener('click', function () { open(); });
    closeBtn.addEventListener('click', close);
    // Clicking anywhere off the image closes it
    box.addEventListener('click', function (event) {
      if (event.target === box) close();
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && box.classList.contains('is-open')) close();
    });
  }



  /* ----------------------------------------------------------------------
     14. Citation markers
     Rewrites plain [1] / [5][6] in article copy as superscript references.
     Rich text fields strip <sup>, so this has to happen at render. Works on
     text nodes and builds real DOM nodes, so copy can never break markup.
     --------------------------------------------------------------------*/
  function initCitations(scope) {
    var roots = (scope || document).querySelectorAll('[data-emw-cites="true"]:not([data-cites-ready])');
    if (!roots.length) return;

    var RE = /(\s?)\[(\d{1,3})\]/g;

    roots.forEach(function (root) {
      root.dataset.citesReady = 'true';

      // Collect text nodes first — rewriting while walking would revisit
      // the nodes we just inserted.
      var nodes = [];
      (function walk(node) {
        for (var child = node.firstChild; child; child = child.nextSibling) {
          if (child.nodeType === 3) {
            if (child.nodeValue.indexOf('[') !== -1) nodes.push(child);
          } else if (child.nodeType === 1) {
            var tag = child.tagName;
            if (tag !== 'SUP' && tag !== 'A' && tag !== 'SCRIPT' && tag !== 'STYLE') walk(child);
          }
        }
      })(root);

      nodes.forEach(function (node) {
        var text = node.nodeValue;
        RE.lastIndex = 0;
        if (!RE.test(text)) return;
        RE.lastIndex = 0;

        var frag = document.createDocumentFragment();
        var last = 0, match;

        while ((match = RE.exec(text)) !== null) {
          if (match.index > last) {
            frag.appendChild(document.createTextNode(text.slice(last, match.index)));
          }
          // The captured space is dropped — a superscript should sit tight
          // against the word it follows.
          var sup = document.createElement('sup');
          sup.className = 'emw-cite';
          sup.textContent = match[2];
          frag.appendChild(sup);
          last = match.index + match[0].length;
        }

        if (last < text.length) {
          frag.appendChild(document.createTextNode(text.slice(last)));
        }
        node.parentNode.replaceChild(frag, node);
      });
    });
  }


  /* ----------------------------------------------------------------------
     15. Upsell modal
     Intercepts the buy box add-to-cart, offers the kit, then adds either
     both items or just the pump. Both go in a single /cart/add.js call so
     they arrive together and the customer can't end up with a half-added
     cart if something fails between two requests.
     --------------------------------------------------------------------*/
  // Set by the buy box; read by the upsell so every route to the cart or
  // checkout carries the code.
  var discountCode = '';

  // Belt and braces. Applying the code to the session covers the whole visit;
  // appending ?discount= to the destination guarantees it lands even if the
  // session call was blocked. Both are cheap and neither can double-apply —
  // Shopify treats a repeat as the same single code.
  function withDiscount(path) {
    if (!discountCode) return path;
    return path + (path.indexOf('?') === -1 ? '?' : '&') +
           'discount=' + encodeURIComponent(discountCode);
  }

  var upsellSeen = false;
  var upsellOpen = null;   // set by initUpsell once the modal is wired

  function getUpsell(variantId) {
    var el = document.querySelector('[data-emw-upsell]');
    if (!el) return null;
    if (el.dataset.enabled !== 'true') return null;
    if (el.dataset.offerReady !== 'true') return null;

    // Only offer the upgrade on the size it was configured for. Anyone who
    // picked a different size sees nothing — showing an offer that swaps them
    // to the wrong size would be worse than not offering at all.
    if (String(el.dataset.baseVariant) !== String(variantId)) return null;

    return el;
  }

  function initUpsell(scope) {
    var modal = (scope || document).querySelector('[data-emw-upsell]:not([data-emw-ready])');
    if (!modal) return;
    modal.dataset.emwReady = 'true';

    var lastFocus = null;
    var pending = null;   // { variantId, quantity }

    function close() {
      modal.classList.remove('is-open');
      document.documentElement.classList.remove('emw-modal-open');
      var done = function () { modal.hidden = true; };
      if (reduceMotion) done(); else setTimeout(done, 280);
      if (lastFocus) lastFocus.focus();
    }

    function go(where) {
      if (where === 'checkout') { window.location.href = withDiscount('/checkout'); return; }
      if (where === 'cart') { window.location.href = withDiscount('/cart'); return; }
      close();
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }

    function addItems(items, after, button) {
      var original = button ? button.textContent : '';
      if (button) { button.disabled = true; button.textContent = 'Adding…'; }

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('add failed');
          return res.json();
        })
        .then(function () {
          // Tell the buy box, whichever path added this — accept, decline or
          // the X. On the 'stay' path this is the only thing that turns the
          // button into a way forward.
          document.dispatchEvent(new CustomEvent('emw:cart-added', {
            detail: { variantId: items && items[0] && items[0].id }
          }));
          go(after);
        })
        .catch(function () {
          if (button) { button.disabled = false; button.textContent = original || button.dataset.defaultText || ''; }
          // Never strand the customer — fall back to the cart.
          window.location.href = withDiscount('/cart');
        });
    }

    function open(detail) {
      pending = detail;
      lastFocus = document.activeElement;
      modal.hidden = false;
      requestAnimationFrame(function () { modal.classList.add('is-open'); });
      document.documentElement.classList.add('emw-modal-open');
      var accept = modal.querySelector('[data-emw-upsell-accept]');
      if (accept) accept.focus();
    }

    // Exposed so the buy box can call it directly rather than relying on a
    // listener being attached in time.
    upsellOpen = open;

    /* The pinned footer button duplicates the accept button in the hero.
       Showing both at once reads as a mistake, so the footer stows itself
       while the hero button is on screen and slides back when it scrolls
       away.

       The markup ships with the footer visible. We only add the armed class
       once the observer is actually attached, so if this block is skipped —
       stale bundle, no IntersectionObserver, an exception — the customer is
       left with a working button rather than a collapsed empty bar. */
    (function armFooter() {
      var foot = modal.querySelector('[data-emw-upsell-foot]');
      var inline = modal.querySelector('.emw-upsell__accept');
      var scroller = modal.querySelector('[data-emw-upsell-scroll]');
      if (!foot || !inline || !scroller) return;
      if (typeof window.IntersectionObserver !== 'function') return;

      try {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            foot.classList.toggle('is-stowed', entry.isIntersecting);
          });
        }, { root: scroller, threshold: 0 });

        io.observe(inline);
        modal.classList.add('emw-upsell--armed');
        // The hero button is on screen when the modal opens, so start stowed.
        foot.classList.add('is-stowed');
      } catch (e) {
        // Leave the footer visible and unarmed.
        modal.classList.remove('emw-upsell--armed');
        foot.classList.remove('is-stowed');
      }
    })();

    modal.querySelectorAll('[data-emw-upsell-accept]').forEach(function (button) {
      button.addEventListener('click', function () {
        if (!pending) return;
        // The kit variant already includes the pump, so add the kit alone —
        // adding both would charge for two pumps.
        addItems([{ id: Number(modal.dataset.kitVariant), quantity: pending.quantity }],
                 modal.dataset.acceptAction || 'cart', button);
      });
    });

    function decline(button, where) {
      if (!pending) { close(); return; }
      addItems([{ id: pending.variantId, quantity: pending.quantity }],
               where || modal.dataset.declineAction || 'cart', button);
    }

    modal.querySelectorAll('[data-emw-upsell-decline]').forEach(function (button) {
      button.addEventListener('click', function () { decline(button); });
    });

    // Every dismissal still adds the pump — it was already on its way to the
    // cart when the modal intercepted, so declining the upgrade must not lose
    // the thing the customer actually asked for.
    //
    // The X is the one path that stays put: it means "not now", not "take me
    // away". go('stay') closes the modal and fires cart:refresh, so the header
    // cart count ticks up and the customer can see the pump landed without
    // leaving the page. Escape is the keyboard form of that same button and
    // behaves identically — splitting them would leave keyboard users with no
    // way to dismiss without navigating.
    modal.querySelectorAll('[data-emw-upsell-close]').forEach(function (button) {
      button.addEventListener('click', function () { decline(null, 'stay'); });
    });

    // The backdrop and the explicit decline link follow decline_action.
    modal.querySelectorAll('[data-emw-upsell-dismiss]').forEach(function (button) {
      button.addEventListener('click', function () { decline(null); });
    });
    modal.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') decline(null, 'stay');
      if (event.key !== 'Tab') return;
      var focusable = modal.querySelectorAll('button:not([disabled])');
      if (!focusable.length) return;
      var first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
  }

  /* ----------------------------------------------------------------------
     Downsell — exit-intent survey. Desktop only: there is no honest exit
     signal without a cursor. Survey first, then the answer routes to the
     offer panel, the size panel, or a thank-you.

     It may only fire when ALL of these hold:
       - enabled, and this is a fine-pointer device
       - nothing in the cart (they are buying — leave them alone)
       - no other emw modal is open
       - not already shown this session, not inside the suppression window
       - engaged: (dwell AND depth) OR they opened Size Info, which is a
         stronger signal than either
     --------------------------------------------------------------------*/
  function initDownsell(scope) {
    var modal = (scope || document).querySelector('[data-emw-downsell]:not([data-emw-ready])');
    if (!modal) return;
    modal.dataset.emwReady = 'true';

    if (modal.dataset.enabled !== 'true') return;
    if (!mq('(hover: hover) and (pointer: fine)')) return;
    if (entryPanel === 'survey' && !modal.querySelector('[data-emw-downsell-reason]')) return;

    // Which panel it opens on. The survey markup is always present; this
    // only decides what the visitor sees first.
    var entryPanel = modal.dataset.entryPanel === 'offer' ? 'offer' : 'survey';

    var dwellMs = (parseInt(modal.dataset.minDwell, 10) || 30) * 1000;
    var minDepth = (parseInt(modal.dataset.minDepth, 10) || 40) / 100;
    var suppressDays = parseInt(modal.dataset.suppressDays, 10);
    if (isNaN(suppressDays)) suppressDays = 7;

    // Once per session; quiet after a dismissal. Storage is best-effort —
    // a blocked store just means the gate leans on the session flag alone.
    try { if (window.sessionStorage.getItem('emw-downsell') === 'shown') return; } catch (e) {}
    if (suppressDays > 0) {
      try {
        var dismissedAt = parseInt(window.localStorage.getItem('emw-downsell-dismissed'), 10);
        if (dismissedAt && Date.now() - dismissedAt < suppressDays * 86400000) return;
      } catch (e) {}
    }

    var startedAt = Date.now();
    var maxDepth = 0;
    var sizeInfoSeen = false;
    var cartCount = parseInt(modal.dataset.cartCount, 10) || 0;
    var opened = false;
    var lastFocus = null;

    function measureDepth() {
      var doc = document.documentElement;
      var total = doc.scrollHeight - window.innerHeight;
      if (total <= 0) { maxDepth = 1; return; }
      var ratio = (window.pageYOffset || doc.scrollTop || 0) / total;
      if (ratio > maxDepth) maxDepth = ratio;
    }
    var depthTick = false;
    window.addEventListener('scroll', function () {
      if (depthTick) return;
      depthTick = true;
      requestAnimationFrame(function () { depthTick = false; measureDepth(); });
    }, { passive: true });
    measureDepth();

    document.addEventListener('emw:size-info', function () { sizeInfoSeen = true; });

    // Track the cart the same way the buy box does: optimistic bump on our
    // own adds, reconcile on the theme's cart events.
    document.addEventListener('emw:cart-added', function () { cartCount += 1; });
    function syncCart() {
      if (typeof window.fetch !== 'function') return;
      fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (cart) { if (cart) cartCount = cart.item_count || 0; })
        .catch(function () {});
    }
    ['cart:change', 'cart:refresh'].forEach(function (name) {
      document.addEventListener(name, syncCart);
      document.documentElement.addEventListener(name, syncCart);
    });

    function emit(action, extra) {
      var detail = { action: action };
      if (extra) { for (var k in extra) { detail[k] = extra[k]; } }
      document.dispatchEvent(new CustomEvent('emw:downsell', { detail: detail }));
    }

    function eligible() {
      if (opened || cartCount > 0) return false;
      if (document.documentElement.classList.contains('emw-modal-open')) return false;
      if (Date.now() - startedAt >= dwellMs && maxDepth >= minDepth) return true;
      return sizeInfoSeen;
    }

    function showPanel(name) {
      modal.querySelectorAll('[data-emw-downsell-panel]').forEach(function (panel) {
        panel.hidden = panel.dataset.emwDownsellPanel !== name;
      });
      var target = modal.querySelector('[data-emw-downsell-panel="' + name + '"]');
      if (!target) return;
      var focusable = target.querySelector('button:not([disabled]), a[href], [tabindex="-1"]');
      if (focusable) focusable.focus();
    }

    function open() {
      opened = true;
      try { window.sessionStorage.setItem('emw-downsell', 'shown'); } catch (e) {}
      lastFocus = document.activeElement;
      modal.hidden = false;
      requestAnimationFrame(function () { modal.classList.add('is-open'); });
      document.documentElement.classList.add('emw-modal-open');
      showPanel(entryPanel);
      emit('shown', { panel: entryPanel });
    }

    function close() {
      modal.classList.remove('is-open');
      document.documentElement.classList.remove('emw-modal-open');
      var done = function () { modal.hidden = true; };
      if (reduceMotion) done(); else setTimeout(done, 280);
      try { window.localStorage.setItem('emw-downsell-dismissed', String(Date.now())); } catch (e) {}
      if (lastFocus && lastFocus.focus) lastFocus.focus();
      emit('dismissed');
    }

    // The exit signal: the cursor leaving the page through the top edge,
    // toward the URL bar and the close button. relatedTarget is null only
    // when it actually left the document.
    document.addEventListener('mouseout', function (event) {
      if (event.relatedTarget) return;
      if (event.clientY > 10) return;
      if (!eligible()) return;
      open();
    });

    modal.querySelectorAll('[data-emw-downsell-close]').forEach(function (button) {
      button.addEventListener('click', close);
    });

    modal.querySelectorAll('[data-emw-downsell-reason]').forEach(function (button) {
      button.addEventListener('click', function () {
        var response = button.dataset.emwDownsellReason || 'message';
        emit('reason', { reason: button.textContent.trim(), response: response });
        if (response === 'offer') { showPanel('offer'); return; }
        if (response === 'size') { showPanel('size'); return; }
        // Thanks: swap in this reason's own message if it has one, then
        // close on our own — the visitor is done with us.
        var custom = modal.querySelector('[data-thanks-for="' + button.dataset.blockId + '"]');
        var fallback = modal.querySelector('[data-thanks-default]');
        modal.querySelectorAll('[data-thanks-for], [data-thanks-default]').forEach(function (el) {
          el.hidden = true;
        });
        if (custom) { custom.hidden = false; }
        else if (fallback) { fallback.hidden = false; }
        showPanel('thanks');
        setTimeout(close, 2200);
      });
    });

    var accept = modal.querySelector('[data-emw-downsell-accept]');
    if (accept) {
      accept.addEventListener('click', function () {
        emit('accepted');
        var variantId = accept.dataset.variantId;
        var url = accept.dataset.url;
        // No discount parameter here on purpose: the page's code is scoped
        // to the HydroXtreme 7 variants and would read as "not applicable"
        // on a cart holding only this product.
        if (variantId && typeof window.fetch === 'function') {
          accept.disabled = true;
          accept.textContent = 'Adding…';
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
          })
            .then(function (res) {
              if (!res.ok) throw new Error('add failed');
              window.location.href = '/cart';
            })
            .catch(function () {
              if (url) { window.location.href = url; return; }
              accept.disabled = false;
              accept.textContent = accept.dataset.defaultText || 'Order now';
            });
          return;
        }
        if (url) window.location.href = url;
      });
    }

    // Size rows: each button adds its resolved variant and goes to the cart.
    // Plain '/cart', no ?discount= — the session already carries the page's
    // code from load, so the 7 discounts on its own and the 8/9 are honestly
    // full price rather than promised a code that doesn't cover them.
    modal.querySelectorAll('[data-emw-downsell-size]').forEach(function (button) {
      button.addEventListener('click', function () {
        var variantId = Number(button.dataset.emwDownsellSize);
        if (!variantId) return;
        emit('accepted', { variantId: variantId, via: 'size' });

        // No fetch: Shopify's GET add endpoint does the same add-then-cart.
        var getAdd = '/cart/add?id=' + variantId + '&quantity=1&return_to=%2Fcart';
        if (typeof window.fetch !== 'function') { window.location.href = getAdd; return; }

        button.disabled = true;
        button.textContent = 'Adding…';
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
        })
          .then(function (res) {
            if (!res.ok) throw new Error('add failed');
            window.location.href = '/cart';
          })
          .catch(function () { window.location.href = getAdd; });
      });
    });

    // Escape closes; Tab stays inside. Links count as focusable here — the
    // size panel's CTA is an <a>.
    modal.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') { close(); return; }
      if (event.key !== 'Tab') return;
      var focusable = modal.querySelectorAll('a[href], button:not([disabled])');
      if (!focusable.length) return;
      var first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
  }

  /* ---------------------------------------------------------------------- */
  function initAll(scope) {
    (scope || document).querySelectorAll('.emw-explorer').forEach(initExplorer);
    (scope || document).querySelectorAll('.emw-video').forEach(initVideo);
    (scope || document).querySelectorAll('.emw-progress').forEach(initProgress);
    // The upsell must be wired before the buy box, otherwise the very first
    // add-to-cart can fire at a modal whose listener isn't attached yet.
    initUpsell(scope);
    initDownsell(scope);
    (scope || document).querySelectorAll('.emw-gallery').forEach(initGallery);
    (scope || document).querySelectorAll('.emw-gallery').forEach(initZoom);
    (scope || document).querySelectorAll('.emw-buy').forEach(initBuyBox);
    (scope || document).querySelectorAll('.emw-vcard').forEach(initVideoCard);
    (scope || document).querySelectorAll('.emw-tgrid-wrap').forEach(initGrid);
    (scope || document).querySelectorAll('.emw-faq').forEach(initFaq);
    initReveal(scope);
    initCounters(scope);
    initCitations(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Shopify theme editor
  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
  document.addEventListener('shopify:section:select', function (event) { initAll(event.target); });
})();
